import React, { useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Chip,
  Grid,
  Stack,
  Typography,
  LinearProgress
} from '@material-ui/core';

import { getPlaceholders } from 'constants/text/placeholders';
import CandidateCardContainer from 'components/containers/CandidateCardContainer';
import CandidateModalContainer from 'components/containers/CandidateModalContainer';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import styled from '@emotion/styled';
import { updateDataByUrl } from 'requests';
import useLocales from 'hooks/useLocales';
import { CardWrapper } from 'styled';
import { Title1 } from 'components/text/title';
import MButton from 'components/new-designer/button/MButton';
import EmptyContent from 'components/EmptyContent';

interface SkillSet {
  title: string;
  data: string[];
}

interface Skills {
  hard_skills: SkillSet;
  soft_skills: SkillSet;
}

interface CandidateSkillsCardProps {
  title: string;
  type: string;
  isCandidate: boolean;
  candidateId: string;
  editSkillsModalOpen: boolean;
  setEditSkillsModalOpen: (open: boolean) => void;
  initialHardSkills: SkillSet;
  initialSoftSkills: SkillSet;
  hardSkills: SkillSet;
  softSkills: SkillSet;
  isEditable: boolean;
  onInit: () => void;
  style?: any;
  skillsStreaming?: boolean;
  skillsProgress?: { extracted: number; total: number };
  isCvUploadModal?: boolean;
}

const CustomAutocomplete = styled(Autocomplete)((props: any) => ({
  '.MuiChip-root': {
    background: !props?.eventInterview ? '#9B5FDA' : 'rgba(93, 56, 137, 0.11)',
    borderRadius: '30px',
    padding: '10px 20px',
    height: 'auto',
    fontFamily: 'Rubik',
    fontWeight: 700,
    fontSize: '14px',
    color: !props?.eventInterview ? '#FFFFFF' : '#562C82',

    '& svg': props?.eventInterview && {
      background: 'url(/static/icons/e-remove.svg) no-repeat right center',
      width: 'max-content',
      '& g': {
        display: 'none'
      }
    }
  },
  '& .MuiAutocomplete-clearIndicator': {
    display: 'none'
  }
}));

export const CandidateSkillsCard = ({
  title,
  type,
  isCandidate,
  candidateId,
  editSkillsModalOpen,
  setEditSkillsModalOpen,
  initialHardSkills,
  initialSoftSkills,
  hardSkills,
  softSkills,
  isEditable,
  onInit,
  style,
  skillsStreaming = false,
  skillsProgress = { extracted: 0, total: 0 },
  isCvUploadModal = false
}: CandidateSkillsCardProps) => {
  const { NO_DATA } = getPlaceholders();
  const { t } = useLocales();

  // Local state for skills - single source of truth
  const [skills, setSkills] = useState<Skills>({
    hard_skills: {
      title: 'hard_skills',
      data: initialHardSkills.data || []
    },
    soft_skills: {
      title: 'soft_skills',
      data: initialSoftSkills.data || []
    }
  });

  // Track if skills have been modified
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUserEditing, setIsUserEditing] = useState(false);

  // Update skills when initial props change (e.g., after CV upload)
  const updateSkillsFromProps = useCallback(() => {
    setSkills({
      hard_skills: {
        title: 'hard_skills',
        data: initialHardSkills.data || []
      },
      soft_skills: {
        title: 'soft_skills',
        data: initialSoftSkills.data || []
      }
    });
    setHasChanges(false);
  }, [initialHardSkills.data, initialSoftSkills.data]);

  // Handle skill changes in modal
  const handleSkillChange = useCallback((skillType: keyof Skills, newSkills: string[]) => {
    setIsUserEditing(true);
    setSkills(prev => ({
      ...prev,
      [skillType]: {
        ...prev[skillType],
        data: newSkills
      }
    }));
    setHasChanges(true);
  }, []);

  // Save skills to database
  const handleSave = useCallback(async () => {
    if (!isCandidate || isSaving) return;

    setIsSaving(true);
    
    try {
      const requestBody = {
        hard_skills: skills.hard_skills.data,
        soft_skills: skills.soft_skills.data
      };

      const response = await updateDataByUrl(
        `/api/candidate/${candidateId}/`,
        requestBody
      );

      // Refresh candidate data from database to update initial skills
      onInit();
      
      // Update initial skills to match what was saved
      // This will trigger updateSkillsFromProps via useEffect
      setHasChanges(false);
      
      // Close modal
      setEditSkillsModalOpen(false);
      setIsUserEditing(false);
      
    } catch (error) {
      // Handle error - could show snackbar or error message
    } finally {
      setIsSaving(false);
    }
  }, [isCandidate, candidateId, skills, setEditSkillsModalOpen, isSaving]);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (hasChanges) {
      // Ask user if they want to save changes
      const shouldSave = window.confirm('You have unsaved changes. Do you want to save them?');
      if (shouldSave) {
        handleSave();
        return;
      }
    }
    
    // Reset to initial state
    updateSkillsFromProps();
    setIsUserEditing(false);
    setEditSkillsModalOpen(false);
  }, [hasChanges, handleSave, updateSkillsFromProps, setEditSkillsModalOpen]);

  // Update skills when initial props change
  React.useEffect(() => {
    updateSkillsFromProps();
  }, [updateSkillsFromProps]);

  // Merge Redux streaming skills with local state
  React.useEffect(() => {
    if (!isUserEditing && (hardSkills.data.length > 0 || softSkills.data.length > 0)) {
      setSkills(prev => {
        const newHardSkills = [...new Set([...prev.hard_skills.data, ...hardSkills.data])];
        const newSoftSkills = [...new Set([...prev.soft_skills.data, ...softSkills.data])];
        
        // Check if Redux skills are different from initial skills (indicating CV extraction)
        const hasNewHardSkills = hardSkills.data.some(skill => !initialHardSkills.data.includes(skill));
        const hasNewSoftSkills = softSkills.data.some(skill => !initialSoftSkills.data.includes(skill));
        
        if (hasNewHardSkills || hasNewSoftSkills) {
          setHasChanges(true);
        }
        
        return {
          hard_skills: {
            title: 'hard_skills',
            data: newHardSkills
          },
          soft_skills: {
            title: 'soft_skills',
            data: newSoftSkills
          }
        };
      });
    }
  }, [hardSkills.data, softSkills.data, isUserEditing, initialHardSkills.data, initialSoftSkills.data]);

  // Set hasChanges to true when skills are streamed from Redux (CV extraction)
  React.useEffect(() => {
    if (skillsStreaming && (hardSkills.data.length > 0 || softSkills.data.length > 0)) {
      setHasChanges(true);
    }
  }, [skillsStreaming, hardSkills.data.length, softSkills.data.length]);

  // Set hasChanges to true when modal opens due to CV upload
  React.useEffect(() => {
    if (editSkillsModalOpen && isCvUploadModal) {
      setHasChanges(true);
    }
  }, [editSkillsModalOpen, isCvUploadModal]);

  // Set hasChanges to true when modal opens and there are Redux skills
  React.useEffect(() => {
    if (editSkillsModalOpen && (hardSkills.data.length > 0 || softSkills.data.length > 0)) {
      setHasChanges(true);
    }
  }, [editSkillsModalOpen, hardSkills.data.length, softSkills.data.length]);

  // Skill color logic - for skills card display
  const getSkillColor = useCallback((skill: string, skillType: 'hard_skills' | 'soft_skills') => {
    // All skills in the card are from database, so they're all "initial"
    if (skillType === 'hard_skills') {
      return '#14a741'; // Green for hard skills from database
    }
    return '#F4A261'; // Orange for soft skills from database
  }, []);

  // Skill color logic - for modal editing
  const getModalSkillColor = useCallback((skill: string, skillType: 'hard_skills' | 'soft_skills') => {
    const isInitial = initialHardSkills.data?.includes(skill) || initialSoftSkills.data?.includes(skill);
    
    if (skillType === 'hard_skills') {
      return isInitial ? '#14a741' : '#1C9DD7';
    }
    return isInitial ? '#F4A261' : '#E76F51';
  }, [initialHardSkills.data, initialSoftSkills.data]);

  const getSkillSetTitle = (skillSet: string) =>
    skillSet.includes('soft') ? 'Soft skills' : 'Hard skills';

  // Skills card should always display database skills, not local editing state
  const displaySkills = {
    hard_skills: {
      title: 'hard_skills',
      data: initialHardSkills.data || []
    },
    soft_skills: {
      title: 'soft_skills',
      data: initialSoftSkills.data || []
    }
  };
  
  const skillsArray = [displaySkills.hard_skills, displaySkills.soft_skills];
  const hasAnySkills = skillsArray.some(skillSet => skillSet.data.length > 0);

  return (
    <CandidateCardContainer
      title={title}
      withoutBox={false}
      onEdit={isEditable ? () => setEditSkillsModalOpen(true) : null}
      type={type}
      style={style}
    >
      <CandidateModalContainer open={editSkillsModalOpen} onClose={handleClose}>
        {isEditable && (
          <CardWrapper sx={{ boxShadow: 'none' }}>
            <Title1 mb="10px">{t('Skills')}</Title1>
            
            {[skills.hard_skills, skills.soft_skills].map((skillSet, idx) => (
              <CustomAutocomplete
                key={idx}
                multiple
                id={`tags-filled-${idx}`}
                options={[]}
                value={skillSet.data || []}
                freeSolo
                onChange={(e: any, newValue: any) => {
                  handleSkillChange(
                    skillSet.title as keyof Skills,
                    newValue
                  );
                }}
                sx={{
                  background: '#ffffff',
                  borderRadius: '10px',
                  mt: idx > 0 ? 2 : 0
                }}
                loadingText="loading"
                popupIcon={
                  <KeyboardArrowDownIcon style={{ color: '#562C82' }} />
                }
                renderTags={(value: string[], getTagProps: any) =>
                  value.map((option: string, index: number) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    const backgroundColor = getModalSkillColor(option, skillSet.title as 'hard_skills' | 'soft_skills');
                    return (
                      <Chip
                        variant="outlined"
                        label={option}
                        key={key}
                        {...tagProps}
                        style={{
                          color: 'white',
                          fontWeight: 500,
                          backgroundColor
                        }}
                      />
                    );
                  })
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={getSkillSetTitle(skillSet.title)}
                    placeholder="Start typing and press enter"
                  />
                )}
              />
            ))}
            
            <MButton
              title={isSaving ? "Saving..." : "Save my skills"}
              MyClass="violetButton"
              style={{ 
                width: '100%', 
                marginTop: '25px', 
                height: '48px',
                opacity: isSaving ? 0.7 : 1
              }}
              click={handleSave}
              disabled={isSaving}
            />
          </CardWrapper>
        )}
      </CandidateModalContainer>

      {skillsStreaming && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('Extracting skills')}... {skillsProgress.extracted}/
            {skillsProgress.total}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={
              (skillsProgress.extracted / Math.max(skillsProgress.total, 1)) *
              100
            }
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      <Box sx={{ width: '100%', display: 'flex', columnGap: '40px' }}>
        {hasAnySkills ? (
          <CardWrapper
            sx={{
              boxShadow: 'none',
              padding: '20px 0px'
            }}
          >
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap'
              }}
            >
              {skillsArray.map((skillSet, idx) => (
                <Grid key={idx} mb={idx === 0 ? 2 : 0}>
                  <Typography mb="10px">
                    {getSkillSetTitle(skillSet.title)}
                  </Typography>
                  <Stack
                    spacing={1}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap'
                    }}
                  >
                    {skillSet.data?.map((skill: string, skillIdx: number) => (
                      <Chip
                        key={skillIdx}
                        label={skill}
                        style={{
                          color: 'white',
                          fontWeight: 500,
                          backgroundColor: getSkillColor(skill, skillSet.title as 'hard_skills' | 'soft_skills'),
                          margin: 0
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardWrapper>
        ) : (
          <EmptyContent title={NO_DATA} />
        )}
      </Box>
    </CandidateCardContainer>
  );
};